import { cn } from "@workspace/web-ui/lib/utils";
import { Calendar, CircleCheck, Clock, Info, LucideIcon, Pill } from "lucide-react";

// import { EncounterTagStatusType, TAG_STYLES } from "@workspace/web-ui/lib/encounter";
import { FormattedEncounter } from "../types";
import { ConsultationStatus } from "../../quick-actions/components/consultation-status";
import { Image } from "@workspace/web-ui/components/image";

interface EncounterCardProps {
  item: FormattedEncounter;
  selectedEncounter?: FormattedEncounter | null
  setSelectedEncounter?: (state: FormattedEncounter) => void;
  onOpenChange?: (value: boolean) => void;
  program?: string
}

const CARD_COLORS = {
  medication: "bg-[#00C781]",
  appointment: "bg-[#4272DD]",
  consultation: "bg-[#7B238D]"
} as const;

type InfoRowProps = { Icon: LucideIcon | string, alt: string, value: string, testId: string, style?: string, isHappening?: string | null, isRefill?: boolean }

const InfoRow = ({ Icon, alt, value, testId, style }: InfoRowProps) => (
  <div className={cn("flex flex-row gap-2 items-center rounded-[10px] px-[6px] py-[4px] w-fit", style)} data-testid={testId}>
    {typeof Icon === "string" ? (
      <Image className={cn("w-3 h-3 sm:w-4 sm:h-4 text-[#7F8493]")} src={Icon} aria-label={alt} layout="constrained" width={16} height={16} />
    ) : (
      <Icon className={cn("w-3 h-3 sm:w-4 sm:h-4 text-[#7F8493] font-bold")} aria-label={alt} />
    )}
    <span className={cn("text-xs sm:text-sm font-normal text-[#7F8493] whitespace-normal break-words")}>{value}</span>
  </div>
);

const EncounterAppointmentsCard = (props: EncounterCardProps) => {
  const { date } = props.item;

  const meetingInfo = [
    // { Icon: User, alt: "calendar", value: patient.name, testId: "patient-name" },
    { Icon: Clock, alt: "clock", value: date || "No time specified", testId: "encounter-date" },
    // { Icon: Video, style: TAG_STYLES[status as EncounterTagStatusType], alt: tag || "happening-now", value: tag || "No time specified", testId: tag || "happening-now" }
  ];

  return (
    <div className="flex flex-col gap-2">
      {meetingInfo.map((info) => (
        <InfoRow key={info.testId} {...info} />
      ))}
    </div>
  );
};

const EncounterRefillsCard = (props: EncounterCardProps) => {
  const { date, medicalRefills } = props.item;

  const meetingInfo = [
    // {
    //   Icon: User,
    //   alt: "patient-name",
    //   value: patient.name,
    //   testId: "patient-name"
    // },
    {
      Icon: Calendar,
      alt: "meeting-date",
      value: date || "No time specified",
      testId: "meeting-date"
    },
    {
      Icon: Pill,
      alt: "pill",
      value: medicalRefills ? medicalRefills?.map(refill => refill.name).join(", ") : "No medication details",
      testId: "pill-name"
    }
  ];

  return (
    <div className="flex flex-col gap-2 h-fit">
      {meetingInfo.map((info) => (
        <InfoRow key={info.testId} {...info} />
      ))}
    </div>
  );
};

const EncounterConsultationsCard = (props: EncounterCardProps) => {
  const { item } = props;

  const meetingInfo = [
    {
      Icon: "/icons/meeting-now.svg",
      alt: "calendar",
      value: item.date || "No time specified",
      testId: "consultation-date"
    }
    // {
    //   Icon: "/icons/weightloss-program-icon.svg",
    //   alt: "consultation",
    //   value: props.program === "urgent-care" ? "Urgent Care" : "Weightloss program",
    //   testId: "consultation"
    // },
  ];

  return (
    <div className="flex flex-col gap-2 h-fit">
      {meetingInfo.map((info) => (
        <InfoRow key={info.testId} {...info} />
      ))}
    </div>
  );
};

export const EncounterCard = (props: EncounterCardProps) => {
  const { selectedEncounter, setSelectedEncounter, onOpenChange, item } = props
  const { id, title, type } = item;

  const cardColor = type === "appointment" ? CARD_COLORS.appointment : type === "consultation" ? CARD_COLORS.consultation : CARD_COLORS.medication;
  const cardsText = title;

  // TODO: This is a temporary fix to ensure that the encounter data is set correctly.
  const handleClick = () => {
    if (setSelectedEncounter && item) {
      setSelectedEncounter(item);
      onOpenChange?.(true);
    }
  };

  const isSelected = type === "appointment" ? id === selectedEncounter?.id : type === "refill" ? id === selectedEncounter?.id : type === "consultation" ? id === selectedEncounter?.id : false;

  const recommendedTasksSource = (selectedEncounter && selectedEncounter.id === item.id
    ? selectedEncounter.recommendedTasks
    : item.recommendedTasks) as any[] | undefined;

  const normalizedTasks = Array.isArray(recommendedTasksSource) ? recommendedTasksSource : [];

  // const hasPendingMedication = normalizedTasks.some((task) => {
  //   const medicationList = Array.isArray(task?.medications)
  //     ? task.medications
  //     : Array.isArray(task?.prescription?.medication)
  //       ? task.prescription.medication
  //       : [];

  //   if (Array.isArray(medicationList) && medicationList.length > 0) {
  //     return medicationList.some(
  //       (medication: any) => typeof medication?.status === "string" && medication.status.toLowerCase() === "pending"
  //     );
  //   }

  //   return typeof task?.status === "string" && task.status.toLowerCase() === "pending";
  // });

  // Check for medication_payment tasks to determine resolved/action needed status
  const medicationPaymentTasks = normalizedTasks.filter(
    (task: any) => task?.type === "medication_payment"
  );

  // There may be multiple medication_payment tasks.
  const anyCompletedPayment = medicationPaymentTasks.some(
    (task: any) => task?.status === "completed"
  );
  const anyNotCompletedPayment = medicationPaymentTasks.some(
    (task: any) => task?.status !== "completed"
  );

  // Find the first completed task for erx check
  const completedPaymentTask = medicationPaymentTasks.find(
    (task: any) => task?.status === "completed"
  );

  // showResolvedTag only if ALL are completed and is_erx_sent is true for the completed one.
  // showActionNeededTag if ANY are incomplete, OR if all are completed but is_erx_sent is NOT true for the completed one.
  const showResolvedTag = completedPaymentTask?.is_erx_sent === true && !anyNotCompletedPayment;
  const showActionNeededTag =
    (anyNotCompletedPayment && anyCompletedPayment) || // some completed, some not
    (anyCompletedPayment && !anyNotCompletedPayment && completedPaymentTask?.is_erx_sent !== true); // all completed, but is_erx_sent is NOT true
  const showInProgressTag =
    !anyCompletedPayment && anyNotCompletedPayment;

  // Check if appointment has passed based on status
  const isAppointmentPast = type === "appointment" &&
    (item.status === "past" || item.status === "attended" || item.status === "cancelled");

  return (
    <div className="w-full flex h-fit">
      <div
        className={cn("w-[6px] h-auto rounded-tl-[8px] rounded-bl-[8px]", cardColor)}
        role="presentation"
      />
      <div
        className={cn(
          "w-full flex justify-between items-start p-4 border border-[#E6E6E6] rounded-tr-[8px] rounded-br-[8px] shadow-sm bg-white cursor-pointer transition-colors h-fit",
          isSelected && "border-r-[2px] border-t-[2px] border-b-[2px]",
          isSelected && type === "appointment" && "border-[#4272DD]",
          isSelected && type === "refill" && "border-[#00C781]",
          isSelected && type === "consultation" && "border-[#7B238D]",
        )}
        onClick={() => handleClick()}
      >
        <div className="flex flex-col gap-4">
          <div className="flex gap-2 items-center">
            {type === "consultation" && (
              <div className="flex gap-2 items-center">
                <ConsultationStatus
                  tag="Consultation"
                  program={props.program}
                />
                {showResolvedTag && (
                  <div
                    className="flex flex-row gap-1 items-center rounded-full px-[6px] py-[4px] w-fit text-[#16A34A] bg-[#ECFDF3]"
                    data-testid="consultation-resolved-status"
                  >
                    <CircleCheck className="size-4 text-[#16A34A]" />
                    <span className="text-xs font-medium">Resolved</span>
                  </div>
                )}
                {showActionNeededTag && (
                  <div
                    className="flex flex-row gap-1 items-center rounded-full px-[6px] py-[4px] w-fit text-[#FFA726] bg-[#FFF5E6]"
                    data-testid="consultation-action-needed-status"
                  >
                    <Info className="size-4 text-[#FFA726]" />
                    <span className="text-xs font-medium">Action needed</span>
                  </div>
                )}
                {showInProgressTag && (
                  <div
                    className="flex flex-row gap-1 items-center rounded-full px-[6px] py-[4px] w-fit text-[#2563EB] bg-[#EFF6FF]"
                    data-testid="consultation-in-progress-status"
                  >
                    <Clock className="size-4 text-[#2563EB]" />
                    <span className="text-xs font-medium">In progress</span>
                  </div>
                )}
              </div>
            )}
            {type === "appointment" && (
              <div className="flex items-center gap-2">
                <div
                  className={cn(
                    "flex flex-row gap-[2px] items-center rounded-[888px] px-[6px] py-[4px] w-fit border",
                    props.item.tag?.toLowerCase() === "held"
                      ? "text-[#16A34A] bg-[#16A34A26]"
                      : props.item.tag?.toLowerCase() === "not held"
                        ? "text-[#DC2626] bg-[#DC262626]"
                        : "text-[#7F8493] bg-[#EDF0F3]" // grey styling for anything else
                  )}
                  data-testid="consultation-status"
                >
                  <Calendar className={cn(
                    "size-4",
                    props.item.tag?.toLowerCase() === "held"
                      ? "text-[#16A34A]"
                      : props.item.tag?.toLowerCase() === "not held"
                        ? "text-[#DC2626]"
                        : "text-[#7F8493]"
                  )} />
                  <span className="text-xs font-medium">{props.item.tag || "Not Held"}</span>
                </div>
                {(props.item.tag?.toLowerCase() === "held" || props.item.tag?.toLowerCase() === "not held" || isAppointmentPast) && (
                  props.item.recommendedTasks && props.item.recommendedTasks.length > 0 ? (
                    <div
                      className={cn(
                        "flex flex-row gap-[2px] items-center rounded-[888px] px-[6px] py-[4px] w-fit",
                        "text-[#7B238D] bg-[#7B238D26]"
                      )}
                      data-testid="consultation-status"
                    >
                      <Clock className="size-4 text-[#7B238D]" />
                      <span className="text-xs font-medium">{"Resolved"}</span>
                    </div>
                  ) : props.item.tag?.toLowerCase() === "not held" ? null : (
                    <div
                      className={cn(
                        "flex flex-row gap-[2px] items-center rounded-[888px] px-[6px] py-[4px] w-fit",
                        "text-[#FFA726] bg-[#FFF5E6]"
                      )}
                      data-testid="consultation-status"
                    >
                      <Clock className="size-4 text-[#FFA726]" />
                      <span className="text-xs font-medium">{"Action needed"}</span>
                    </div>
                  )
                )}
              </div>
            )}
            {type === "refill" && (
              <div className="flex items-center gap-2">
                <span className="border-[#00C78133] text-[#00C781] text-xs font-medium py-[2px] px-[6px] rounded-[888px] border bg-[#E6FAF2] w-fit flex gap-1">
                  <Pill className="w-3.5 h-3.5 text-[#00C781]" />
                  Refill
                </span>
                {(() => {
                  // For weight-loss, show status based on recommendedTasks and refill status
                  if (props.program === "weight-loss") {
                    const tasks = normalizedTasks;
                    
                    // If rejected, only show rejected tag, no task status tags
                    if (item.status === "rejected") {
                      return (
                        <span className="px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-600 border border-red-200">
                          Rejected
                        </span>
                      );
                    }
                    
                    // Refill status tags (approved/fulfilled) - shown separately
                    const refillStatusTag = (() => {
                      if (item.status && item.status !== "requested") {
                        if (item.status === "approved") {
                          return null
                        }
                        
                        return (
                          <span
                            key="fulfilled"
                            className={cn(
                              "ml-2 px-2 py-1 rounded-full text-xs font-semibold",
                              item.status === "fulfilled"
                                ? "bg-blue-100 text-blue-700 border border-blue-200"
                                : item.status === "partially_fulfilled"
                                  ? "bg-yellow-100 text-yellow-700 border border-yellow-200"
                                  : "bg-gray-100 text-gray-500 border border-gray-200"
                            )}
                          >
                            {item.status === "fulfilled"
                              ? "Fulfilled"
                              : item.status === "partially_fulfilled"
                                ? "Partially Fulfilled"
                                : item.status?.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                          </span>
                        );
                      }
                      return null;
                    })();
                    
                    // Task status tags (in progress/action needed) - shown separately
                    // Always show task status tag for weight-loss (unless rejected)
                            const taskStatusTag = (() => {
                                const hasTasks = Array.isArray(tasks) && tasks.length > 0;
                                const hasPendingTask = hasTasks && tasks.some((task: any) => task?.status === "pending");
                                const isErxSent = hasTasks && tasks.some((task: any) => task?.is_erx_sent === true);

                                let statusToShow = "Action needed";
                                let statusClasses = "text-[#FFA726] bg-[#FFF5E6]";
                                let Icon = null;

                                if (hasPendingTask) {
                                    statusToShow = "In progress";
                                    statusClasses = "text-[#2563EB] bg-[#EFF6FF]";
                                    Icon = Clock;
                                } else if (isErxSent) {
                                    statusToShow = "Resolved";
                                    statusClasses = "text-[#00C781] bg-[#E6FAF2]";
                                    Icon = CircleCheck;
                                }

                                return (
                                    <span key="task-status" className={`px-2 py-1 rounded-full text-xs font-semibold ${statusClasses} flex items-center gap-1`}>
                                        {Icon && <Icon className="w-3 h-3" />}
                                        {statusToShow}
                                    </span>
                                );
                            })();
                    
                    // Return both tags - taskStatusTag always shows, refillStatusTag only if exists
                    return (
                      <>
                        {refillStatusTag}
                        {taskStatusTag}
                      </>
                    );
                  }
                  
                  // For urgent-care or other programs, show original status
                  if (item.status && item.status !== "requested") {
                    return (
                      <span
                        className={cn(
                          "px-2 py-1 rounded-full text-xs font-semibold",
                          item.status === "rejected"
                            ? "bg-red-100 text-red-600 border border-red-200"
                            : item.status === "fulfilled"
                              ? "bg-blue-100 text-blue-700 border border-blue-200"
                              : item.status === "partially_fulfilled"
                                ? "bg-yellow-100 text-yellow-700 border border-yellow-200"
                                : "bg-gray-100 text-gray-500 border border-gray-200"
                        )}
                      >
                        {item.status === "rejected"
                          ? "Rejected"
                          : item.status === "fulfilled"
                            ? "Fulfilled"
                            : item.status === "partially_fulfilled"
                              ? "Partially Fulfilled"
                              : item.status?.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                      </span>
                    );
                  }
                  return null;
                })()}
              </div>
            )}

          </div>
          <div className="flex md:items-center items-start gap-2">
            <h2 className="text-xl font-medium text-[#202020CC] whitespace-normal">{cardsText}</h2>
            {props.program === "urgent-care" ? (
              item.patient.urgent_care_onboarded && item.patient.identity_verified && (
                <div className="">
                  <Image src="/icons/check.svg" alt="timeline" layout="constrained" className="w-4 h-4 sm:w-5 sm:h-5" width={96} height={96} />
                </div>
              )
            ) : (
              item.patient.medical_plan?.payment_status === "paid" && item.patient.glp1_guide_accepted && item.patient.identity_verified && item.patient.supplementary_health_assessment && Object.keys(item.patient.supplementary_health_assessment).length > 0 && (
                <div className="">
                  <Image src="/icons/check.svg" alt="timeline" layout="constrained" className="w-4 h-4 sm:w-5 sm:h-5" width={96} height={96} />
                </div>
              )
            )}
          </div>
          {type === "appointment" && <EncounterAppointmentsCard {...props} />}
          {type === "refill" && <EncounterRefillsCard {...props} />}
          {type === "consultation" && <EncounterConsultationsCard {...props} />}
          {/* new status badge */}
          {/* <div className="flex items-center w-fit gap-2 bg-[#FFF6E7] rounded-lg p-2">
            <h2 className="text-xs font-medium text-[#FFAB1F]">NEW</h2>
          </div> */}
        </div>


        {/* status badge */}
        {/* <div className="flex items-center gap-2 bg-[#FFF6E7] rounded-lg p-2">
          <h2 className="text-xs font-medium text-[#FFAB1F]">NEW</h2>
        </div> */}

      </div>
    </div>
  );
};

